import React, { useContext } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  brandColours,
  fontWeights,
  fontSize,
  zIndexLayers,
  visuallyHidden,
  standardTransition,
} from '../styles';
import { Button } from './ui';
import CartItem from './CartItem';
import ProductCard from './ProductCard';
import { discountPrice, formatPrice } from '../utils';
import { StoreContext } from '../context/StoreContext';

const StyledCart = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${zIndexLayers.fifth};
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
  transition: ${standardTransition('transform')};

  ${minBreakpointQuery.small`
    transition-duration: 0.4s;
  `}

  ${minBreakpointQuery.large`
    transition-duration: 0.6s;
  `}
`;

const StyledInner = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  padding: 0 30px;
  height: 100%;
  width: 100%;
  max-width: 400px;
  background-color: ${standardColours.white};

  ${minBreakpointQuery.large`
    padding-right: 40px;
    padding-left: 40px;
    max-width: 420px;
  `}
`;

const StyledHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid ${brandColours.quaternary};
`;

const StyledHeading = styled.h3`
  ${fontSize(16)};
`;

const StyledClose = styled.button`
  background: none;
  border: none;

  &:before {
    content: '✕';
    color: ${standardColours.black};
    ${fontSize(22)};
    line-height: 1;
  }

  span {
    ${visuallyHidden()};
  }
`;

const StyledItems = styled.ul`
  margin: 20px 0;
`;

const StyledItem = styled.li`
  padding: 18px 0;
  border-bottom: 1px solid ${brandColours.quaternary};
`;

const StyledLabel = styled.label`
  color: ${brandColours.primary};
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  resize: none;
  margin-top: 15px;
  padding: 10px;
`;

const StyledText = styled.p`
  margin: 18px 0;
  ${fontSize(22)};
  text-align: center;

  strong {
    font-weight: ${fontWeights.semibold};
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledCrossSellProducts = styled.div`
  margin: 20px 0;

  ${minBreakpointQuery.large`
    position: absolute;
    top: 0;
    right: 420px;
    left: 0;
    display: flex;
    align-items: center;
    margin: 200px auto;
    padding-right: 30px;
    padding-left: 30px;
    max-width: 1000px;
  `}
`;

const StyledCrossSellProductsInner = styled.div`
  ${({ isOpen }) => {
    if (isOpen) {
      return css`
        ${minBreakpointQuery.large`
          transition: ${standardTransition('opacity', 0.4)} 1.2s;
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.large`
          opacity: 0;
        `}
      `;
    }
  }}
`;

const StyledCrossSellProductsHeading = styled.h3`
  ${fontSize(16)};

  ${minBreakpointQuery.large`
    color: ${standardColours.white};
    ${fontSize(20)};
    text-transform: uppercase;
  `}

  ${minBreakpointQuery.xxlarge`
    ${fontSize(22)};
  `}
`;

const StyledCrossSellProductsItems = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-top: 15px;

  ${minBreakpointQuery.large`
    gap: 20px;
    margin-top: 20px;
  `}
`;

const StyledCrossSellProductsItem = styled(ProductCard)`
  ${minBreakpointQuery.large`
    padding: 20px;
    background-color: ${standardColours.white};
    border-radius: 15px;
  `}
`;

const Cart = ({ crossSellProducts }) => {
  const { allShopifyProductVariant } = useStaticQuery(graphql`
    query CartQuery {
      allShopifyProductVariant(
        filter: {
          availableForSale: { eq: true }
          product: { status: { eq: ACTIVE } }
        }
      ) {
        nodes {
          shopifyId
          quantityDiscounts: metafield(
            key: "quantity_discounts"
            namespace: "custom"
          ) {
            value
          }
          product {
            quantityDiscounts: metafield(
              key: "quantity_discounts"
              namespace: "custom"
            ) {
              value
            }
          }
        }
      }
    }
  `);

  const { isCartOpen, toggleCartOpen, updateCheckoutAttributes, checkout } =
    useContext(StoreContext);

  const { lineItems: initialLineItems, webUrl } = checkout;

  const lineItems = initialLineItems.map(lineItem => {
    const productVariant = allShopifyProductVariant.nodes.find(
      ({ shopifyId }) => shopifyId === lineItem.variant.id,
    );

    let lineItemDiscount = 0;

    if (
      productVariant &&
      (productVariant.quantityDiscounts ||
        productVariant.product.quantityDiscounts)
    ) {
      const quantityDiscounts = productVariant.quantityDiscounts
        ? JSON.parse(productVariant.quantityDiscounts.value)
        : JSON.parse(productVariant.product.quantityDiscounts.value);

      quantityDiscounts.forEach(quantityDiscount => {
        const [quantity, discount] = quantityDiscount.split('|');

        if (lineItem.quantity >= quantity) {
          lineItemDiscount = discount;
        }
      });
    }

    return {
      ...lineItem,
      variant: {
        ...lineItem.variant,
        price: {
          amount: discountPrice(
            lineItem.variant.price.amount,
            lineItemDiscount,
          ),
        },
        discount: parseInt(lineItemDiscount),
      },
    };
  });

  const subtotalPrice = lineItems.reduce(
    (
      subtotal,
      {
        quantity,
        variant: {
          price: { amount },
        },
      },
    ) => subtotal + quantity * amount,
    0,
  );

  const handleOnBlur = async e => {
    await updateCheckoutAttributes([
      { key: 'Special instructions', value: e.target.value },
    ]);
  };

  return (
    <StyledCart
      isOpen={isCartOpen}
      onClick={e => {
        if (e.target === e.currentTarget) {
          if (isCartOpen) {
            toggleCartOpen();
          }
        }
      }}
    >
      <StyledInner>
        <StyledHeader>
          <StyledHeading>
            Basket ({lineItems.length} Item{lineItems.length > 1 && 's'})
          </StyledHeading>
          <StyledClose title="Close" onClick={toggleCartOpen}>
            <span>Close</span>
          </StyledClose>
        </StyledHeader>
        {lineItems.length > 0 ? (
          <>
            <StyledText>
              Basket Subtotal <strong>{formatPrice(subtotalPrice)}</strong>
            </StyledText>
            <StyledButton
              onClick={() => {
                lineItems.some(
                  lineItem =>
                    lineItem.variant.discount > 0 ||
                    lineItem.customAttributes.find(
                      ({ key }) => key === 'Graphic Design',
                    ),
                )
                  ? fetch('/.netlify/functions/shopifyorder', {
                      method: 'POST',
                      body: JSON.stringify({
                        lineItems: lineItems,
                      }),
                    })
                      .then(response => response.json())
                      .then(data => {
                        window.location.href = data.checkoutUrl;
                      })
                      .catch(e => console.log(e))
                  : (window.location.href = webUrl);
              }}
            >
              Checkout Now
            </StyledButton>
            <StyledItems>
              {lineItems.map(item => (
                <StyledItem key={item.id}>
                  <CartItem product={item} />
                </StyledItem>
              ))}
            </StyledItems>
            <StyledLabel>
              Deadline or specific delivery instructions?
              <StyledTextArea
                id="special-instructions"
                name="special-instructions"
                placeholder="If you have a deadline or specific delivery instructions, please let us know here (We will follow our standard lead times if no date is provided)."
                onBlur={e => handleOnBlur(e)}
              />
            </StyledLabel>
            {crossSellProducts && crossSellProducts.length > 0 && (
              <StyledCrossSellProducts>
                <StyledCrossSellProductsInner isOpen={isCartOpen}>
                  <StyledCrossSellProductsHeading>
                    People also bought
                  </StyledCrossSellProductsHeading>
                  <StyledCrossSellProductsItems>
                    {crossSellProducts.map((crossSellProduct, id) => (
                      <StyledCrossSellProductsItem
                        key={id}
                        {...crossSellProduct}
                        onClick={() => toggleCartOpen(false)}
                      />
                    ))}
                  </StyledCrossSellProductsItems>
                </StyledCrossSellProductsInner>
              </StyledCrossSellProducts>
            )}
          </>
        ) : (
          <>
            <StyledText>Your Basket is Empty</StyledText>
            <Button onClick={toggleCartOpen}>Continue Shopping</Button>
          </>
        )}
      </StyledInner>
    </StyledCart>
  );
};

export default Cart;
